import { formatNumber, pluralize, tooltipProps } from '$app/utils'
import Pagination from '$components/Pagination'
import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { useApiListingParams } from '$hooks/actions'
import { useAppRoleService } from '$hooks/services'
import { useAppDetailStore } from '$hooks/stores'
import Header from '$layouts/LayoutWithSidebar/Header'
import { redirect, ROUTE_NAMES } from '$router/config'
import { TAppRoleListResponse } from '$services/api'
import { flexColumn, flexRow } from '$styles/common.css'
import { Button } from '@genie-fintech/ui/components'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import { ListTree, Plus, SquareArrowOutUpRight, Table2 } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { innerMain, main } from './styles.css'
import SearchBox from '$components/SearchBox'
import EmptyData from '$components/EmptyData'
import Table, { Column } from '$components/Table'
import Link from '$router/Link'
import { tableCellLink } from '$components/Table/styles.css'
import { useBreadcrumb } from '$components/Breadcrumb/hooks/useBreadcrumb'
import { useGroupFilter } from '$blocks/GroupFilter/useGroupFilter'
import GroupFilter from '$blocks/GroupFilter'

type ColumnData = TAppRoleListResponse['data'][number]

const { colors } = themeVars

const AppRoleList = () => {
  const appName = useAppDetailStore(state => state.appDetail?.name)

  const { route } = useRouteSummary()
  const { appId } = route.params

  const {
    fetchAppRoleList,
    fetchingAppRoleList,
    roles: { list, meta }
  } = useAppRoleService()

  const {
    searchValue,
    updateSearchValue,
    debouncedSearchValue,
    pagerProps,
    perPage
  } = useApiListingParams({ total: meta?.total })

  const { currentPage: page, jump } = pagerProps

  const { filteredGroupIds, onChangeFilteredGroupIds } = useGroupFilter(() =>
    jump(1)
  )

  useBreadcrumb([{ name: 'Roles' }])

  useEffect(() => {
    if (!appId) return
    fetchAppRoleList({
      page,
      per_page: perPage,
      application_id: appId,
      q: debouncedSearchValue,
      group_ids: filteredGroupIds
    })
  }, [
    appId,
    page,
    perPage,
    debouncedSearchValue,
    filteredGroupIds,
    fetchAppRoleList
  ])

  const handleOnClickAddNew = useCallback(() => {
    redirect(ROUTE_NAMES.APP_ROLE_CREATE, options => options)
  }, [])

  const handleOnClickImport = useCallback(() => {
    redirect(ROUTE_NAMES.APP_ROLES_CSV_IMPORT, options => options)
  }, [])

  const isEmpty = !list.length && !fetchingAppRoleList

  const columns: Column<ColumnData>[] = [
    {
      name: 'No',
      render: ({ index }) => (page - 1) * perPage + index + 1
    },
    {
      name: 'Role Name',
      dataIndex: 'name',
      render: ({ record }) => (
        <Link
          to={ROUTE_NAMES.APP_ROLE_DETAIL}
          options={({ params, ...options }) => ({
            ...options,
            params: { ...params, roleId: record.id }
          })}
          className={tableCellLink}
        >
          {record.name}
          <SquareArrowOutUpRight size={16} />
        </Link>
      )
    },
    {
      name: 'Display Name',
      dataIndex: 'display_name',
      render: ({ record }) => record.display_name ?? '-'
    },
    {
      name: 'Group',
      dataIndex: 'group',
      render: ({ record }) => record.group.name ?? '-'
    },
    {
      name: 'Users',
      dataIndex: 'user_count',
      render: ({ record }) => formatNumber(record.user_count)
    },
    {
      name: 'Permissions',
      dataIndex: 'permission_count',
      render: ({ record }) => formatNumber(record.permission_count)
    }
  ]

  return (
    <>
      <Header style={{ gap: 8 }}>
        <ListTree size={20} />

        <span className={title.two} style={{ paddingLeft: 4 }}>
          Roles
        </span>

        <span style={{ flex: 1 }} />

        <Button
          styleVariants={{ kind: 'neutral', type: 'outlined', size: 'small' }}
          onClick={handleOnClickImport}
          disabled
          {...tooltipProps('Coming soon')}
        >
          <Table2 size={14} />
          Import CSV File
        </Button>

        <Button onClick={handleOnClickAddNew} styleVariants={{ size: 'small' }}>
          <Plus size={16} />
          Add Role
        </Button>
      </Header>

      <main className={main}>
        <article className={innerMain}>
          <header className={flexColumn} style={{ gap: 4 }}>
            <p className={title.two}>{appName}'s ROLES</p>
            <p className={footnote.one} style={{ color: colors.neutral[60] }}>
              Define and assign roles to control access and permissions within
              the system.
            </p>
          </header>

          <article className={flexColumn} style={{ gap: 16, flex: 1 }}>
            <article className={flexRow} style={{ gap: 4 }}>
              <article style={{ flex: 1, maxWidth: 560 }}>
                <SearchBox
                  value={searchValue}
                  onChange={updateSearchValue}
                  placeholder="Search here..."
                />
              </article>

              <GroupFilter
                appId={appId}
                filteredGroupIds={filteredGroupIds}
                onChangeFilteredGroupIds={onChangeFilteredGroupIds}
              />

              <span style={{ flex: 1 }} />

              {!!meta?.total && (
                <span
                  className={title.six}
                  style={{
                    color: colors.text.disabled,
                    textTransform: 'uppercase'
                  }}
                >
                  {pluralize(meta.total, 'role')}
                </span>
              )}
            </article>

            {isEmpty && <EmptyData type="role" onClick={handleOnClickAddNew} />}

            {!isEmpty && (
              <Table
                columns={columns}
                data={list}
                loading={fetchingAppRoleList}
              />
            )}

            {!!list.length && (
              <Pagination meta={meta} pagerProps={pagerProps} />
            )}
          </article>
        </article>
      </main>
    </>
  )
}

export default AppRoleList
